import { useState, useMemo, useCallback, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useFormStore } from "@/store/form-builder";
import {
  evaluateAllLogic,
  getPageJumpToId,
} from "@/components/features/FormBuilder/utils/evaluateLogic";
import {
  buildAnswerSchema,
  getVisibleQuestions,
  scrollToFirstError,
} from "@/components/features/FormBuilder/utils/answerValidation";

import {
  NO_PROVIDER_TYPE,
  buildProviderTypeContext,
} from "@/components/features/FormBuilder/data/contextTriggers";

import type { FormPage } from "@/components/features/FormBuilder/types/form.types";

export type { EvalResult } from "@/components/features/FormBuilder/utils/evaluateLogic";

export function usePreviewForm(pagesOverride?: FormPage[]) {
  const { pages: storePages, setIsPreview, previewPageIndex, setPreviewPageIndex } = useFormStore();
  const pages = pagesOverride ?? storePages;
  const [viewMode, setViewMode] = useState<"desktop" | "mobile">("desktop");
  const [sigModal, setSigModal] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  /** When false, preview skips Zod validation so users can move between pages freely. */
  const [withValidation, setWithValidation] = useState(false);
  const [previewProviderType, setPreviewProviderType] = useState(NO_PROVIDER_TYPE);
  const logicContext = useMemo(
    () => buildProviderTypeContext(previewProviderType),
    [previewProviderType],
  );

  const formMethods = useForm<Record<string, any>>({
    defaultValues: {},
    mode: "onTouched",
    reValidateMode: "onChange",
  });

  const { control, setValue, getValues, setError, clearErrors, formState } = formMethods;
  const { errors } = formState;

  useEffect(() => {
    if (!withValidation) clearErrors();
  }, [withValidation, clearErrors]);

  const answers = getValues();
  const evalResult = useMemo(
    () => evaluateAllLogic(pages, answers, logicContext),
    [pages, answers, logicContext],
  );
  const visiblePages = useMemo(
    () => pages.filter((p) => !evalResult.hiddenPages.has(p.id)),
    [pages, evalResult.hiddenPages],
  );
  const curIdx = Math.min(previewPageIndex, visiblePages.length - 1);
  const curPage = visiblePages[curIdx];
  const isLastPage = curIdx >= visiblePages.length - 1;
  const hasErrors = Object.keys(errors).length > 0;
  const stepperSteps = visiblePages.map((p) => ({ id: p.id, label: p.name }));

  const curPageVisibleQs = useMemo(
    () => getVisibleQuestions(curPage, evalResult),
    [curPage, evalResult],
  );

  const curPageSchema = useMemo(() => buildAnswerSchema(curPageVisibleQs), [curPageVisibleQs]);

  const allVisibleQs = useMemo(
    () => visiblePages.flatMap((p) => getVisibleQuestions(p, evalResult)),
    [visiblePages, evalResult],
  );

  const allPagesSchema = useMemo(() => buildAnswerSchema(allVisibleQs), [allVisibleQs]);

  const validateCurrentPage = useCallback(async (): Promise<boolean> => {
    if (!withValidation) return true;

    const data = getValues();
    const result = curPageSchema.safeParse(data);
    if (result.success) {
      curPageVisibleQs.forEach(({ q }) => clearErrors(q.id));
      return true;
    }

    curPageVisibleQs.forEach(({ q }) => {
      clearErrors(q.id);
      if (q.allowOther) clearErrors(`${q.id}__other`);
    });
    for (const issue of result.error.issues) {
      const fieldId = String(issue.path[0]);
      setError(fieldId, { type: "custom", message: issue.message });
    }
    scrollToFirstError(
      result.error.issues.reduce(
        (acc, issue) => {
          const key = String(issue.path[0]);
          if (!acc[key]) acc[key] = issue.message;
          return acc;
        },
        {} as Record<string, string>,
      ),
    );
    return false;
  }, [withValidation, curPageSchema, curPageVisibleQs, getValues, clearErrors, setError]);

  const handleNext = useCallback(async () => {
    const valid = await validateCurrentPage();
    if (!valid) return;

    const pageJumpId = curPage ? getPageJumpToId(curPage, getValues(), logicContext) : null;
    if (pageJumpId) {
      const ji = visiblePages.findIndex((p) => p.id === pageJumpId);
      if (ji >= 0) {
        setPreviewPageIndex(ji);
        return;
      }
    }
    if (!isLastPage) setPreviewPageIndex(curIdx + 1);
  }, [
    validateCurrentPage,
    curPage,
    getValues,
    logicContext,
    visiblePages,
    isLastPage,
    curIdx,
    setPreviewPageIndex,
  ]);

  const handlePrevious = useCallback(() => {
    clearErrors();
    setPreviewPageIndex(curIdx - 1);
  }, [clearErrors, curIdx, setPreviewPageIndex]);

  const handleFormSubmit = useCallback(async () => {
    if (!withValidation) {
      clearErrors();
      setSubmitted(true);
      return;
    }

    const data = getValues();
    const result = allPagesSchema.safeParse(data);

    if (result.success) {
      allVisibleQs.forEach(({ q }) => clearErrors(q.id));
      setSubmitted(true);
      return;
    }

    const errorMap = result.error.issues.reduce(
      (acc, issue) => {
        const key = String(issue.path[0]);
        if (!acc[key]) acc[key] = issue.message;
        return acc;
      },
      {} as Record<string, string>,
    );

    const currentPageFieldIds = new Set(
      curPageVisibleQs.flatMap(({ q }) => (q.allowOther ? [q.id, `${q.id}__other`] : [q.id])),
    );
    const hasCurrentPageErrors = Object.keys(errorMap).some((id) => currentPageFieldIds.has(id));

    if (hasCurrentPageErrors) {
      curPageVisibleQs.forEach(({ q }) => {
        clearErrors(q.id);
        if (q.allowOther) clearErrors(`${q.id}__other`);
      });
      for (const [id, msg] of Object.entries(errorMap)) {
        if (currentPageFieldIds.has(id)) setError(id, { type: "custom", message: msg });
      }
      scrollToFirstError(errorMap);
    } else {
      const firstErrPageIdx = visiblePages.findIndex((p) => {
        const qs = getVisibleQuestions(p, evalResult);
        const qFieldIds = new Set(
          qs.flatMap(({ q }) => (q.allowOther ? [q.id, `${q.id}__other`] : [q.id])),
        );
        return Object.keys(errorMap).some((id) => qFieldIds.has(id));
      });
      if (firstErrPageIdx >= 0) {
        setPreviewPageIndex(firstErrPageIdx);
        requestAnimationFrame(() => {
          const pageQs = getVisibleQuestions(visiblePages[firstErrPageIdx], evalResult);
          const pageQFieldIds = new Set(
            pageQs.flatMap(({ q }) => (q.allowOther ? [q.id, `${q.id}__other`] : [q.id])),
          );
          pageQs.forEach(({ q }) => {
            clearErrors(q.id);
            if (q.allowOther) clearErrors(`${q.id}__other`);
          });
          for (const [id, msg] of Object.entries(errorMap)) {
            if (pageQFieldIds.has(id)) setError(id, { type: "custom", message: msg });
          }
          scrollToFirstError(errorMap);
        });
      }
    }
  }, [
    withValidation,
    allPagesSchema,
    allVisibleQs,
    curPageVisibleQs,
    visiblePages,
    evalResult,
    getValues,
    clearErrors,
    setError,
    setPreviewPageIndex,
  ]);

  const handleStepChange = useCallback(
    async (stepId: string) => {
      const idx = visiblePages.findIndex((p) => p.id === stepId);
      if (idx < 0) return;
      if (idx > curIdx && withValidation) {
        const valid = await validateCurrentPage();
        if (!valid) return;
      }
      clearErrors();
      setPreviewPageIndex(idx);
    },
    [visiblePages, curIdx, withValidation, validateCurrentPage, clearErrors, setPreviewPageIndex],
  );

  const handleSignatureConfirm = useCallback(
    (url: string) => {
      if (!sigModal) return;
      setValue(sigModal, url);
      clearErrors(sigModal);
      setSigModal(null);
    },
    [sigModal, setValue, clearErrors],
  );

  const handleReset = useCallback(() => {
    formMethods.reset({});
    setSubmitted(false);
    setPreviewPageIndex(0);
  }, [formMethods, setPreviewPageIndex]);

  return {
    // Form state
    control,
    errors,
    hasErrors,
    clearErrors,
    withValidation,
    setWithValidation,
    previewProviderType,
    setPreviewProviderType,

    // Page navigation
    curPage,
    curIdx,
    isLastPage,
    visiblePages,
    stepperSteps,
    viewMode,
    setViewMode,

    // Conditional logic
    evalResult,

    // Signature modal
    sigModal,
    setSigModal,

    // Submit state
    submitted,

    // Actions
    setIsPreview,
    handleNext,
    handlePrevious,
    handleFormSubmit,
    handleStepChange,
    handleSignatureConfirm,
    handleReset,
  };
}
